import { Direction } from '@material-ui/core';
import cx from 'classnames';
import moment from 'moment';
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, RouteProps, Switch, useHistory, useLocation } from 'react-router-dom';
import { useAuth0 } from 'src/auth/useAuth0.hook';
import LoadingDefault from 'src/components/LoadingDefault/LoadingDefault';
import LoadingOrError from 'src/components/LoadingOrError/LoadingOrError';
import MaintenancePage from 'src/components/MaintenancePage/MaintenancePage';
import UserNotFound from 'src/components/NoUserComponent/UserNotFound';
import PageNotFound from 'src/components/PageNotFound/PageNotFound';
import {
  OperatorBoxDisplay,
  SelectOperatorComponent,
} from 'src/components/SelectOperatorComponent/SelectOperatorComponent';
import { useCurrencyConversionRate } from 'src/investorSystem/utils/hooks/useCurrencyConversionRate';
import useViewType from 'src/investorSystem/utils/hooks/useViewType';
import { RootState } from 'src/redux/config/rootReducer';
import {
  setCategoriesColors,
  setGqlClient,
  setSelectedLanguage,
  setToken,
} from 'src/redux/slices/general.slice';
import {
  selectAllFilteredInvestments,
  setInvestments,
  setInvestmentsByCategories,
  setInvestmentsByStates,
  setInvestmentTotals,
  setInvestor,
  setNavigationDict,
  setOfferings,
  setOperatorFilterId,
  setRelatedOperatorIds,
  setShouldDisplayOperatorSelection,
  setViewType,
} from 'src/redux/slices/investor.slice';
import { setHeaderBackgroundColor } from 'src/redux/slices/oldTheme.slice';
import { setUser } from 'src/redux/slices/user.slice';
import {
  Investment,
  InvestmentGraphql,
  InvestmentRefEnum,
  Investor,
  InvestorUser,
  Offering,
  Operator,
  OperatorInvestor,
  OperatorInvestorGraphql,
  User,
  UserRolesEnum,
  UserSettings,
} from 'src/types.d';
import { getCategoriesColors } from 'src/utils/functions/entitiesCategoryServices';
import { convertInvestmentsGraphqlToInvestments } from 'src/utils/functions/investmentServices';
import {
  arrangeInvestments,
  arrangeInvestmentTotals,
  arrangeInvestorInvestmentsByArea,
  arrangeInvestorInvestmentsByCategories,
  getAllHoldingsFromInvestmentGraphql,
  getInvestmentOperatorId,
  getInvestmentOperatorName,
  getOrganizationName,
  getViewType,
  updateOverviewTotals,
} from 'src/utils/functions/investorServices';
import { graphQLRequest } from 'src/utils/graphQL/graphQLRequestModule';
import { UPDATE_USER_SETTINGS_QUERY } from 'src/utils/graphQL/queries/general/updateUserSettings';
import { GET_USER_INVESTOR } from 'src/utils/graphQL/queries/investorPortal/getUserInvestor';
import { useGraphQLRequest } from 'src/utils/graphQL/useGraphQLRequest.hook';
import { useIsTermsAgreed } from 'src/utils/hooks/useIsTermsAgreed';
import { LanguageToDirection } from 'src/utils/variables/index';
import SecureRoute from '../../../components/SecureRoutes/SecureRoute';
import Terms from '../../../components/Terms/Terms';
import secureRoutes from '../../routes/routes';
import useDashboardRoutes from '../../utils/hooks/useDashboardRoutes';
import './style.scss';

import RefreshPage from 'src/components/RefreshModal/RefreshPage';
import store from 'src/redux/config/configureStore';
import {
  ALL_OFFERINGS_FILES,
  getAllDraftFiles,
  GetAllEntitiesMainFiles,
  getAllPublicFiles,
} from 'src/redux/slices/fileTagging.service';
import { setOperator, setOverviewKpisCategories } from 'src/redux/slices/operator.slice';
import {
  setBarColors,
  setCategoryColors,
  setColors,
  setFonts,
  setKpiColors,
  setKpiNames,
  setPieColors,
} from 'src/redux/slices/theme.slice';
import { i18nService } from 'src/services/i18n.service';
import { getAndUpdateOfferingActions } from 'src/utils/apiRequests/apiOfferingActions';
import { getOperatorInvestorByEmailService } from 'src/utils/apiRequests/getOperatorInvestorByEmailService';
import { getTextFromMultiLanguage } from 'src/utils/functions/getTextFromMutliLan';
import { GET_THEME } from 'src/utils/graphQL/queries/general/getTheme';
import { GetInvestorOffering } from 'src/utils/graphQL/queries/investorPortal/getOfferings';
import { GetInvestorOfferingByOperator } from 'src/utils/graphQL/queries/investorPortal/getOfferingsByOperator';
import { GET_OPERATOR } from 'src/utils/graphQL/queries/investorPortal/viewAs/getOperatorDetails';
import { GET_USER_ADMIN } from 'src/utils/graphQL/queries/operatorPortal/getUserAdmin';
import useLanguageDirection from 'src/utils/hooks/useLanguageDirection';

const disallowedRoutesForLegalDisclaimer = ['/dashboard/documents'];

let timeOutEntity;

const MainModule = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const _viewType = useViewType();
  const { user, loginWithRedirect, isAuthenticated, getTokenSilently } = useAuth0();
  const { user: userDetails } = useSelector((state: RootState) => state.user);
  const { currencyRates, languageContext, unauthorizedError } = useSelector(
    (state: RootState) => state.general,
  );
  const theme = useSelector((state: RootState) => state.theme);

  const filteredInvestments: Investment[] = selectAllFilteredInvestments(store.getState());

  //Handles whether to show operator selection page or not
  const {
    viewAsEmail,
    shouldDisplayOperatorSelection,
    operatorFilterId,
    navMenuExpandedLock,
    offerings,
    redirectUrl,
  } = useSelector((state: RootState) => state.investor);

  //read the profimex kpis from redux store
  const profimexKpis = useSelector((state: RootState) => state.investor.profimexOverviewTotals);

  const [loadingTheme, setLoadingTheme] = useState<boolean>(true);
  const [loadingState, setLoadingState] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  const [investorDetails, setInvestorDetails] = useState<InvestorUser | null>(null);
  const [termsFailed, setTermsFailed] = useState<boolean>(false);
  const [showNoUserPage, setShowNoUserPage] = useState<boolean>(false);
  const [noUserCheck, setNoUserCheck] = useState<boolean>(false);
  const [showMaintenance, setShowMaintenance] = useState<boolean>(false);

  //renders the screen when we get the theme
  const refreshRef = useRef(false);

  const keyToFunction = {
    colors: setColors,
    barColors: setBarColors,
    pieColors: setPieColors,
    kpiColors: setKpiColors,
    kpiNames: setKpiNames,
    categoryColors: setCategoryColors,
    fonts: setFonts,
  };

  //Holds the operators ids for the operator selection page
  const [operatorsForInvestor, setOperatorsForInvestor] = useState<OperatorBoxDisplay[]>([]);

  //the investments for the legal entities who are alive
  const investments = useMemo(() => {
    //the list of deceased legal entities
    const deceasedList =
      investorDetails?.userLegalEntities
        ?.filter(entity => entity.deceased)
        ?.map(entity => entity.id) ?? [];

    return investorDetails?.investmentDetails?.filter(
      investment => deceasedList?.includes(investment?.legalEntityId) === false,
    );
  }, [investorDetails]);

  const totalSecureRoutes: RouteProps[] = useDashboardRoutes(investments, offerings);
  const { client } = useGraphQLRequest();
  const [shouldShouldLegalDisclaimer, setShouldShouldLegalDisclaimer] = useState(false);
  const location = useLocation();

  //get the language direction
  const direction = useLanguageDirection();

  // Dispatch currency rates
  useCurrencyConversionRate();

  // //Dispatch profimex overview kpis if needed
  // useProfimexOverviewKpis();

  //Check if no user can be found in DB
  useEffect(() => {
    noUserFoundShowDemo();
  }, []);

  //Check if maintenance is needed
  useEffect(() => {
    if (
      (operatorsForInvestor?.every(operator => operator?.isUnderMaintenance) &&
        operatorsForInvestor?.length > 0) ||
      operatorsForInvestor?.find(operator => operator.id === operatorFilterId)?.isUnderMaintenance
    ) {
      setShowMaintenance(true);
    }
  }, [operatorsForInvestor, operatorFilterId]);

  //getting the theme
  useEffect(() => {
    if (!operatorFilterId) {
      setLoadingTheme(false);
      return;
    }

    setLoadingTheme(true); //load new theme

    const getTheme = async () => {
      const token = await getTokenSilently();

      //getting the theme from the server
      const themeRes = await graphQLRequest(
        token,
        client,
        GET_THEME,
        { id: operatorFilterId },
        viewAsEmail ? UserRolesEnum.operator : UserRolesEnum.investor,
      );

      // if the theme is found, set the theme
      if (themeRes.getTheme) {
        // looping over the themes and adding them
        const serverTheme = themeRes.getTheme;

        Object.keys(theme).forEach(key => {
          if (key in serverTheme && serverTheme[key] !== null) {
            dispatch(keyToFunction[key](serverTheme[key]));
          }
        });
      }

      setLoadingTheme(false);
    };

    getTheme();
  }, [operatorFilterId]);

  /**
   * Set the view type according to the operator filter id
   */
  useEffect(() => {
    if (investments?.length > 0) {
      //filter out investment not related to the filtered operator
      const filteredInvestments = operatorFilterId
        ? investments?.filter(investment => investment.operatorDetails?.id === operatorFilterId)
        : investments;

      const viewType = getViewType(filteredInvestments);
      dispatch(setViewType(viewType));

      //getting the operator details
      const operatorDetails =
        filteredInvestments.length > 0 && filteredInvestments[0]?.operatorDetails;

      //setting the kpi Categories for the operator
      const overviewKpisCategories = operatorDetails?.overviewKpisCategories;
      if (overviewKpisCategories?.length > 0) {
        dispatch(setOverviewKpisCategories(overviewKpisCategories));
      }
      initInvestments(filteredInvestments, viewType);
    }
  }, [investments, operatorFilterId]);

  //Check if no user was found in DB - if so show no user page
  async function noUserFoundShowDemo() {
    const investor = !viewAsEmail ? await getInvestorDetailsFromApiRequest() : null;

    //isViewAs
    const isViewAs = viewAsEmail !== null && viewAsEmail !== '';

    if (investor?.data?.findCurrentUser === null && !isViewAs) {
      setShowNoUserPage(true);
    }

    //Set user not found check was made
    setNoUserCheck(true);
  }

  /**
   * Gets the investor offering from the server
   */
  async function updateInvestorOffering() {
    //get the offerings from the server
    const token = await getTokenSilently();

    //get publish or draft offerings
    const res = viewAsEmail
      ? await graphQLRequest(token, client, GetInvestorOfferingByOperator)
      : await graphQLRequest(token, client, GetInvestorOffering);
    const newOffering: Offering[] = viewAsEmail
      ? res.findAllPublicOffersByOperator ?? []
      : res?.findAllPublicOffers ?? [];

    //update the offerings in the store
    if (newOffering) dispatch(setOfferings(newOffering));
  }

  //Start initializing investor details and loadingState after currency rates are updated
  useEffect(() => {
    //pass only if -
    // 1. loading is done.   2. no user check was made and user was found
    // 3. currencies conversion rate were updated
    if (!loadingState || showNoUserPage || !noUserCheck || !currencyRates) return;

    _getInvestorDetails();
    updateInvestorOffering();
  }, [currencyRates, noUserCheck, profimexKpis]);

  /**
   * After offering is updated - get the files for the offering
   */
  useEffect(() => {
    //create async function to update the offering actions
    const updateOfferingActions = async () => {
      //exit if no offerings
      if (!offerings || offerings.length === 0) return;

      //get the files for the offerings
      const token = await getTokenSilently();
      const ids = offerings.map(offering => offering.id);
      //set the id for all files
      viewAsEmail
        ? getAllDraftFiles(token, client, ids, ALL_OFFERINGS_FILES)
        : getAllPublicFiles(token, client, ids, ALL_OFFERINGS_FILES);

      //get all offering actions and store in redux
      getAndUpdateOfferingActions(token, client);
    };

    //update the offering actions
    updateOfferingActions();
  }, [offerings]);

  /**
   * Update the entity files
   */
  useEffect(() => {
    //validate investments
    if (!filteredInvestments || filteredInvestments.length === 0) return;

    //create async function to update the offering actions
    const updateEntityFiles = async () => {
      //exit if no offerings
      if (!filteredInvestments || filteredInvestments.length === 0) return;

      //get the files for the offerings
      const token = await getTokenSilently();
      const ids = filteredInvestments.map(inv => inv.entityDetails?.id).filter(v => v) ?? [];
      GetAllEntitiesMainFiles(!!viewAsEmail, token, client, ids);
    };

    //update the entities main files
    updateEntityFiles();
  }, [filteredInvestments]);

  useEffect(() => {
    location &&
      setShouldShouldLegalDisclaimer(
        !disallowedRoutesForLegalDisclaimer.some(route => location.pathname.includes(route)),
      );
  }, [location]);

  useEffect(() => {
    if (operatorFilterId && operatorFilterId === 'b02c1370-4b9d-11ec-b7b9-a7995e4ae954') {
      dispatch(setHeaderBackgroundColor('#1A3F61'));
    }
  }, [operatorFilterId]);

  useEffect(() => {
    if (userDetails) {
      setLoadingState(false);
    }
  }, [userDetails, currencyRates]);

  useEffect(() => {
    if (!loadingState && languageContext?.language) {
      clearTimeout(timeOutEntity);
      timeOutEntity = setTimeout(() => {
        setWixAnswers(_viewType);
      });
    }
  }, [loadingState, languageContext, _viewType]);

  const setWixAnswers = VT => {
    const language = languageContext?.language === 'heb' ? 'he' : 'en';
    const widget = document.getElementById('answers-widget-launcher-container');
    const e = () => {
      if (widget) {
        widget.remove();
      }
      var ele = document.createElement('script');
      ele.type = 'text/javascript';
      ele.async = !0;
      ele.src = `https://achieved.wixanswers.com/apps/widget/v1/achieved/d5a7d4ce-d78d-411d-8d45-949f703122a7/${language}/embed.js`;

      var t = document.getElementsByTagName('script')[0];
      t.parentNode.insertBefore(ele, t);
    };

    e();
    window.addEventListener
      ? window.addEventListener('load', e)
      : window['attachEvent']('onload', e);
    window['AnswersWidget'] = {
      onLoaded: function (e) {
        window['AnswersWidget'].queue.push(e);
      },
      queue: [],
    };
  };

  /**
   * Extracts the subdomain name before the first ".app" in the given URL.
   *
   * @param redirectUrl - The URL to extract the subdomain from.
   * @returns The subdomain if it exists, otherwise an empty string.
   */
  function extractSubdomain(redirectUrl: string | null): string {
    // Return an empty string if the URL is null or empty
    if (!redirectUrl || redirectUrl === '') {
      return '';
    }

    try {
      // Create a URL object for easy parsing
      const url = new URL(redirectUrl);

      // Extract the hostname from the URL
      const hostname = url.hostname?.toLowerCase() ?? '';

      // Check if the hostname contains ".app"
      const appIndex = hostname.indexOf('.app');
      if (appIndex === -1) {
        return ''; // ".app" not found in the hostname
      }

      // Extract the part before ".app"
      const subdomain = hostname.substring(0, appIndex);

      // Check if it's a valid subdomain (not empty and not "www")
      if (subdomain && subdomain !== 'www') {
        return subdomain;
      }

      return ''; // Return an empty string for invalid subdomains
    } catch (e) {
      // If there's an error (e.g., invalid URL), return an empty string
      return '';
    }
  }

  /**
   * Extracts the operator name from the URL.
   * @returns Operator name from the URL
   */
  function getOperatorNameFromUrl() {
    try {
      //fetch the name before the first .app in the redirectUrl is exist
      const nameFromRedirectUrl = extractSubdomain(redirectUrl);
      if (nameFromRedirectUrl && nameFromRedirectUrl !== '') return nameFromRedirectUrl;

      //get the operator name from the domain
      const domain = document?.location?.host ?? '';
      const URLSplit = domain?.split('.');
      let operator = 'achieved';
      if (URLSplit && URLSplit.length > 0 && URLSplit[0] !== 'app') {
        operator = URLSplit[0];
      }
      return operator;
    } catch {
      return 'achieved';
    }
  }

  /**
   * @description converting the investor investment from the graphql to the investment object
   * @param investor the result we got from the graphql query
   * @returns the investments after they were mapped
   */
  const convertInvestorInvestment = investor => {
    // getting the investments from the investor and converting them
    const investmentsBeforeMapping = investor?.investmentDetails
      ? convertInvestmentsGraphqlToInvestments(
          (investor.investmentDetails as InvestmentGraphql[]).map(x => ({
            ...x,
            holdings: getAllHoldingsFromInvestmentGraphql(x),
          })),
        )
      : [];

    // mapping the investments to add the project and fund details
    const investments = investmentsBeforeMapping.map(investment => {
      const dict = {
        [InvestmentRefEnum.fundId]: () => ({
          fundDetails: {
            ...investment.fundDetails,
            entityType: 'Fund' as 'Fund',
          },
        }),
        [InvestmentRefEnum.projectId]: () => ({
          projectDetails: {
            ...investment.projectDetails,
            entityType: 'Project' as 'Project',
          },
        }),
      };

      return { ...investment, ...dict[investment.ref[0].key]() };
    });

    return investments;
  };

  async function _getInvestorDetails() {
    const investor = await getInvestorDetailsFromApiRequest();
    if (investor?.errorCode) {
      if (investor.errorCode === 503) setShowMaintenance(true);
      setError(investor);
      setLoadingState(false);
    } else {
      // getting the investments
      const investments = viewAsEmail
        ? investor?.investmentDetails
        : convertInvestorInvestment(investor);

      //filter out investment not related to the filtered operator
      const filteredInvestments = operatorFilterId
        ? investments?.filter(investment => investment.operatorDetails?.id === operatorFilterId)
        : investments;

      const viewType = getViewType(filteredInvestments);
      viewAsEmail ? moment.locale('en') : initDefaultLanguage(investor);

      const investorData = { ...investor, investmentDetails: filteredInvestments };
      setInvestorDetails(investorData);
      dispatch(setInvestor(investorData));

      initInvestments(filteredInvestments, viewType);

      //setting the investor user if not view as
      if (!viewAsEmail) dispatch(setUser(investor));
      // setting the user for the operator
      else {
        const token = await getTokenSilently();
        const res = await graphQLRequest(
          token,
          client,
          GET_USER_ADMIN,
          null,
          UserRolesEnum.operator,
        );

        if (res.findCurrentUser) dispatch(setUser(res.findCurrentUser));
      }

      //get all operators for the investor
      let operatorBoxData: OperatorBoxDisplay[] = filteredInvestments?.map(investment => {
        return {
          id: investment?.operatorDetails?.id,
          name: investment?.operatorDetails?.name,
          isUnderMaintenance: investment?.operatorDetails?.isUnderMaintenance,
        };
      });

      //remove duplicates by id
      operatorBoxData =
        operatorBoxData?.filter((v, i, a) => a.findIndex(t => t.id === v.id) === i) ?? [];

      //get all available operators ids from investments
      let operatorsIds: string[] = operatorBoxData?.map(operatorBoxDate => operatorBoxDate?.id);

      //remove duplicates
      operatorsIds = operatorsIds?.filter((v, i, a) => a.indexOf(v) === i) ?? [];
      setOperatorsForInvestor(operatorBoxData);
      dispatch(setRelatedOperatorIds(operatorsIds));

      //set the first operator id as the default operator id or set shouldDisplayOperatorSelection to true
      //This will initiate the operator selection page
      if (operatorBoxData?.filter(operator => !operator.isUnderMaintenance).length === 1)
        dispatch(
          setOperatorFilterId(operatorBoxData?.find(operator => !operator.isUnderMaintenance)?.id),
        );
      else {
        // getting the operator name from the operator
        const operatorName = getOperatorNameFromUrl();

        // finding the operator id by the operator name
        const operatorId =
          operatorBoxData.length > 0
            ? operatorBoxData?.find(
                item =>
                  item.name?.toLocaleLowerCase().trim() ===
                  operatorName?.toLocaleLowerCase().trim(),
              )?.id ?? null
            : null;

        // if the operator id is found, set it as the operator filter id
        operatorId
          ? dispatch(setOperatorFilterId(operatorId))
          : dispatch(setShouldDisplayOperatorSelection(true));
      }

      //todo nir this might cause problem and could potentially be in graphQLRequest
      //todo need to think about it further
      //set token and gql client
      const token = await getTokenSilently();
      dispatch(setToken(process.env.REACT_APP_TOKEN ?? token));
      dispatch(setGqlClient(client));
    }
  }

  async function getInvestorDetailsFromApiRequest() {
    const token = await getTokenSilently();

    //if it is a normal investor
    if (!viewAsEmail) {
      const query = GET_USER_INVESTOR;
      const apiRes = await graphQLRequest(token, client, query, null, UserRolesEnum.investor);
      return apiRes?.findCurrentUser ? apiRes.findCurrentUser : apiRes;
    }

    //getting the operator
    const operatorRes = await graphQLRequest(
      token,
      client,
      GET_OPERATOR,
      null,
      UserRolesEnum.operator,
    );

    const operator =
      operatorRes?.findCurrentOperators?.length > 0 ? operatorRes?.findCurrentOperators[0] : null;

    dispatch(setOperator(operator));

    // getting the investor in the view as mode
    const res = await getOperatorInvestorByEmailService(
      token,
      client,
      operator?.id,
      viewAsEmail,
      false,
    );

    const investor = res as OperatorInvestorGraphql;
    const arrangedInvestor = arrangeInvestors([investor], operator);

    return arrangedInvestor[0];
  }

  function arrangeInvestors(
    investors: OperatorInvestorGraphql[],
    operator?: Operator,
  ): OperatorInvestor[] {
    return investors?.map(investor => ({
      ...investor,
      investmentDetails: arrangeInvestments(
        // @ts-expect-error userLegalEntities are not typed properly
        investor.userLegalEntities?.map(entity => entity.investments).flat(),
        operator,
      ),
    }));
  }

  function initDefaultLanguage(investor: Partial<Investor>) {
    const language = investor?.language;

    //if no language was found - set to english
    if (!language) {
      moment.locale('en');
      return;
    }

    const languageObj = {
      direction: LanguageToDirection[language] as Direction,
      language,
    };
    dispatch(setSelectedLanguage(languageObj));
    moment.locale(language === 'heb' ? 'he' : 'en');
  }

  function initInvestments(investments: Investment[], viewType) {
    dispatch(setInvestments(investments));
    const investmentsByCategories = arrangeInvestorInvestmentsByCategories(investments);
    const investmentsByArea = arrangeInvestorInvestmentsByArea(investments);
    dispatch(setInvestmentsByCategories(investmentsByCategories));
    dispatch(setInvestmentsByStates(investmentsByArea));

    const investmentsTotals = arrangeInvestmentTotals(investments);
    dispatch(setInvestmentTotals(investmentsTotals));

    dispatch(setViewType(viewType));

    const navigationDict = getNavigationDict(investments);
    dispatch(setNavigationDict(navigationDict));

    //Gets the colors for every category (including custom)
    const categoriesColors = getCategoriesColors(investments);
    dispatch(setCategoriesColors(categoriesColors));

    //get all totals for the overview page
    updateOverviewTotals(investments);
  }

  const _renderSecuredRoutes = useCallback(() => {
    if (!loadingState && investments?.length > 0) {
      return secureRoutes.map((route, index) => (
        <SecureRoute
          exact={route.exact}
          path={route.path}
          name={route.name}
          component={route.component}
          key={`secure route ${route.name}-${index}`}
          scopes={route.scopes}
          isAuthenticated={isAuthenticated}
          loginWithRedirect={loginWithRedirect}
          history={history}
          totalSecureRoutes={totalSecureRoutes}
        />
      ));
    }
    return <></>;
  }, [loadingState]);

  /**
   * Updates the user settings in the DB
   * @param termsAgreed Whether the user agreed to the terms
   * @param marketingAgreed Whether the user agreed to marketing
   */
  async function onUserAgreed(termsAgreed: boolean, marketingAgreed: boolean) {
    //don't update if no user was found (could be in a view as mode)
    if (!userDetails?.id) return;
    //TODO - nir should add this?
    // const time = Date.now();

    // const ipDetails = {
    //   ...user.meta_data.geoip,
    //   clientIp: user.meta_data.clientIp,
    // };
    const userSettings: UserSettings = {
      // ipDetails,
      terms: termsAgreed,
      marketing: marketingAgreed,
      // time,
    };

    const updatedUser: User = {
      email: userDetails?.email,
      id: userDetails?.id,
      settings: userSettings,
    };

    const dataToSend = {
      updateUserInput: updatedUser,
    };
    const token = await getTokenSilently();

    try {
      const res = await graphQLRequest(token, client, UPDATE_USER_SETTINGS_QUERY, dataToSend);
      const updatedSettings: UserSettings = res.updateUser.settings;
      const updatedUser = { ...userDetails, settings: updatedSettings };
      dispatch(setUser(updatedUser));
    } catch (error) {
      setError(error);
      setTermsFailed(true);
    }
  }

  const isTermsAgreed = useIsTermsAgreed(userDetails);

  function getNavigationDict(investments) {
    return investments.reduce(
      (acc, investment) => {
        const investmentId = investment.id;

        if (!(acc && acc[investmentId])) {
          const operatorId = getInvestmentOperatorId(investment);
          if (!acc.hasOwnProperty(operatorId)) {
            const operatorName = getInvestmentOperatorName(investment);
            acc[operatorId] = operatorName;
          }

          if (!(acc && acc[investmentId])) {
            const entityName = getOrganizationName(investment);
            acc[investmentId] = entityName;

            //add all updates
            const updates =
              investment.projectDetails?.updatesDetails ?? investment.fundDetails?.updatesDetails;
            if (updates && updates.length > 0) {
              updates.forEach(update => {
                acc[update.id] = getTextFromMultiLanguage(update?.title);
              });
            }
          }
        }
        return acc;
      },
      {
        documents: 'navigation.documentsCenter',
        overview: 'navigation.overview',
        offerings: 'navigation.offerings',
        updates: 'navigation.updates',
        'my-account': 'navigation.myAccount',
      },
    );
  }

  const isInvestments = useMemo(() => (investments?.length ? true : false), [investments]);

  //If the user is not found and this is not a view as - show no user page
  if (showMaintenance) {
    return <MaintenancePage />;
  }

  //If no user was found in DB and this is and operator view as - ask for refresh
  if (!isInvestments && viewAsEmail && viewAsEmail !== '' && !loadingState && !loadingTheme)
    return <RefreshPage />;

  //If no user was found in DB - show no user page
  if (showNoUserPage) return <UserNotFound />;

  //set the class name for an expanded nav menu
  const expandNavClass: string = navMenuExpandedLock ? 'disclaimer-expanded' : '';

  //If the user is not found and this is not a view as - show no user page
  if (unauthorizedError) return <UserNotFound />;

  return (
    <>
      <LoadingOrError
        loading={loadingState || loadingTheme}
        error={error}
        loadingMessage={i18nService.translate('loadingMessage2')}
      >
        <React.Fragment>
          {isTermsAgreed || viewAsEmail !== '' ? (
            isInvestments ? (
              shouldDisplayOperatorSelection ? (
                <SelectOperatorComponent operatorsBoxData={operatorsForInvestor} />
              ) : (
                <>
                  <Suspense fallback={<LoadingDefault />}>
                    <Switch>
                      {_renderSecuredRoutes()}
                      {/* Fallback page not found route - should always be last */}
                      <Route component={PageNotFound} />
                    </Switch>
                  </Suspense>
                  {shouldShouldLegalDisclaimer ? (
                    <div
                      className={cx('legalDisclaimerContainer', {
                        'hebrew-disclaimer': languageContext?.language === 'heb',
                      })}
                    >
                      <div className={`legal-disclaimer-text ${expandNavClass}`}>
                        {i18nService.translate('legalDisclaimer')}
                      </div>
                    </div>
                  ) : null}
                </>
              )
            ) : (
              <UserNotFound />
            )
          ) : (
            <>
              <Redirect to="/" />
              <Terms
                onUserAgreed={onUserAgreed}
                isFailed={termsFailed}
                operators={operatorsForInvestor}
              />
            </>
          )}
        </React.Fragment>
      </LoadingOrError>
    </>
  );
};

export default MainModule;
